import { Function } from '@phosphor-icons/react'
import {
  KeyboardEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { FormBuilderContext } from '@/pages/Contexts/FormBuilderContext'
import { Question } from '@/types/question'
import Tooltip from '@/ui/atoms/Tooltip'
import { FormBuilderPage } from '@/utils/domain/FormBuilder/FormBuilderPage'
import FormBuilderSection from '@/utils/domain/FormBuilder/FormBuilderSection'
import cn from '@/utils/tailwind/cn'

import CalculationAddField from './CalculationAddField'
import { CalculationEditor } from './CalculationEditor'
import { CalculationFunctionModal } from './CalculationFunction'
import { CalculationKeyboard } from './CalculationKeyboard'
import { NUMBERS, OPERATORS } from './constants'
import { generateId, parseFormulaFromEditor } from './utils'
import { buildFunctionDescriptionByFormula } from './utils/buildFunctionDescriptionByFormula'
import { convertFormulaToHtml } from './utils/convertFormulaToHtml'
import { extractQRValues } from './utils/extractQRValues'
import { getMatrixQuestionInfo } from './utils/getMatrixQuestionInfo'
import { getMatrixRowQuestionInfo } from './utils/getMatrixRowQuestionInfo'
import { shouldClearEditorForAGEDETAIL } from './utils/shouldClearEditorForAGEDETAIL'

const buttons = [...NUMBERS, ...OPERATORS, '.']

type CreateCustomDivProps = {
  type: string
  content: string
  contentEditable?: boolean
  formIndex?: string
  rowFormIndex?: string
  description?: string
  value?: string
  id?: string
}

type FieldPropsType = Omit<CreateCustomDivProps, 'type' | 'content'>

type FunctionModalInfoProps = {
  open: boolean
  id?: string
  value?: string
}

type Props = {
  questions: Question[]
  initialValues?: string
  onFormulaChange: (value: string) => void
}

export const CalculationComponent = ({
  questions,
  initialValues,
  onFormulaChange,
}: Props) => {
  const { getSection, getPage } = useContext(FormBuilderContext)

  const [tooltipLabel, setTooltipLabel] = useState<string | undefined>(
    undefined
  )
  const [isFocused, setIsFocused] = useState(false)
  const [isFunctionModalInfo, setIsFunctionModalInfo] = useState<
    FunctionModalInfoProps | undefined
  >(undefined)
  const tooltipRef = useRef<HTMLDivElement | null>(null)
  const mainRef = useRef<HTMLDivElement | null>(null)
  const [lastRange, setLastRange] = useState<Range | null>(null)
  const editorRef = useRef<HTMLDivElement | null>(null)
  const tooltipTimeoutId = useRef<NodeJS.Timeout | null>(null)

  const filteredQuestions = useMemo(
    () =>
      questions.filter((item: Question) => {
        const questionSubtype = item.question_type.question_subtype
        const questionType = item.question_type.question_type
        const isValidSubtype = ['calculation', 'number'].includes(
          questionSubtype
        )

        let section: FormBuilderSection | null = null
        if (item.section) {
          section = getSection(item.section)
        }

        let page: FormBuilderPage | null = null
        if (section) {
          page = getPage(section.page as string)
        }

        const isRepeatSection = page?.repeat_section

        const hasValidAssignValues =
          questionType === 'datetime' || isValidSubtype || item.assign_values

        const isValidType = [
          'matrix',
          'radio',
          'checkbox',
          'datetime',
        ].includes(questionType)

        return (
          (isValidSubtype || isValidType) &&
          hasValidAssignValues &&
          !isRepeatSection
        )
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [questions]
  )

  const setCursorPosition = (element: HTMLElement, position: number) => {
    const range = document.createRange()
    const selection = window.getSelection()
    if (!selection) return
    range.setStart(element.firstChild || element, position)
    range.collapse(true)
    selection.removeAllRanges()
    selection.addRange(range)
  }

  const createCustomDiv = ({
    type,
    content,
    contentEditable,
    formIndex,
    rowFormIndex,
    value,
    description,
    id,
  }: CreateCustomDivProps) => {
    const div = document.createElement('div')

    div.dataset.type = type

    if (contentEditable) div.contentEditable = 'false'
    if (formIndex) div.dataset.formIndex = formIndex
    if (rowFormIndex) div.dataset.rowFormIndex = rowFormIndex
    if (value) div.dataset.value = value
    if (description) div.dataset.description = description
    if (id) div.dataset.id = id

    div.textContent = content
    return div
  }

  const saveCursorPosition = () => {
    const selection = window.getSelection()
    if (selection && selection.rangeCount > 0) {
      setLastRange(selection.getRangeAt(0))
    }
  }

  const restoreCursorPosition = () => {
    if (lastRange) {
      editorRef.current?.focus()
      const selection = window.getSelection()
      selection?.removeAllRanges()
      selection?.addRange(lastRange)
    }
  }

  const insertElement = (
    key: string,
    type: string,
    parent: HTMLElement,
    node: Node,
    offset: number,
    fieldProps?: FieldPropsType
  ) => {
    const editor = editorRef.current
    const newDiv = createCustomDiv({ content: key, type, ...fieldProps })

    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.nodeValue || ''
      const before = text.slice(0, offset)
      const after = text.slice(offset)

      const getDivType = (content: string) => {
        if (/^[+\-*/]$/.test(content)) return 'operator'
        if (/^[()]$/.test(content)) return 'parentheses'
        if (/^[0-9.]+$/.test(content)) return 'number'
        return 'number'
      }

      if (before) {
        const beforeDiv = createCustomDiv({
          content: before,
          type: getDivType(before),
        })
        parent.replaceWith(beforeDiv, newDiv)
      } else {
        parent.replaceWith(newDiv)
      }

      if (after) {
        const afterDiv = createCustomDiv({
          content: after,
          type: getDivType(after),
        })
        newDiv.after(afterDiv)
        setCursorPosition(afterDiv, 0)
      } else {
        setCursorPosition(newDiv, 1)
      }
    } else {
      if (editor && parent === editor) {
        const childNodes = editor.childNodes
        const insertPosition = Math.min(offset, childNodes?.length)
        editor.insertBefore(newDiv, childNodes[insertPosition] || null)
      } else {
        parent.after(newDiv)
      }
      setCursorPosition(newDiv, 1)
    }

    if (['field', 'function'].includes(type)) {
      const placeholder = document.createElement('i')
      newDiv.after(placeholder)

      setTimeout(() => {
        setCursorPosition(placeholder, 0)
      }, 0)
    }

    saveCursorPosition()
    return newDiv
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const editor = editorRef.current
    if (!editor) return

    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) return

    const range = selection.getRangeAt(0)
    let node = range.startContainer
    let offset = range.startOffset

    let parent = node as HTMLElement
    if (node.nodeType === Node.TEXT_NODE && node.parentElement) {
      parent = node.parentElement
    }

    if (!parent || !editor.contains(parent)) return

    if (parent.closest('[contenteditable="false"]')) {
      const safePosition = document.createElement('i')

      parent.parentNode?.insertBefore(safePosition, parent.nextSibling)

      parent = safePosition
      node = safePosition.firstChild || safePosition
      offset = 0

      const newRange = document.createRange()
      newRange.setStart(node, offset)
      newRange.collapse(true)
      selection?.removeAllRanges()
      selection?.addRange(newRange)
    }

    const isNumber = /^[0-9.]+$/.test(event.key)
    const isOperator = /^[+\-*/]$/.test(event.key)
    const isParentheses = /^[()]$/.test(event.key)

    const isAllowedKey = [
      'Backspace',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
    ].includes(event.key)

    const hasAgeDetailWithoutOnlyNumber = shouldClearEditorForAGEDETAIL(
      editor.innerText
    )

    if (hasAgeDetailWithoutOnlyNumber && !isAllowedKey) {
      event.preventDefault()
      return
    }

    if (!isNumber && !isOperator && !isAllowedKey && !isParentheses) {
      event.preventDefault()
      return
    }

    if (isOperator || isParentheses) {
      event.preventDefault()
      insertElement(
        event.key,
        isOperator ? 'operator' : 'parentheses',
        parent,
        node,
        offset
      )
    }

    if (isNumber) {
      event.preventDefault()
      const text = node.nodeValue || ''
      const hasOperatorOrParentheses = /[+\-*/()]/.test(text)
      if (
        parent.dataset.type === 'number' &&
        node.nodeType === Node.TEXT_NODE &&
        !hasOperatorOrParentheses
      ) {
        const beforeText = text.slice(0, offset)
        const afterText = text.slice(offset)

        const newText = beforeText + event.key + afterText
        node.nodeValue = newText
        if (node.parentElement) {
          setCursorPosition(node.parentElement, offset + 1)
        }
      } else {
        insertElement(event.key, 'number', parent, node, offset)
      }
    }
    saveCursorPosition()
  }

  const insertAtCursor = (text: string, type?: 'field' | 'function') => {
    const editor = editorRef.current
    if (!editor) return

    if (type === 'function' && text.startsWith('AGEDETAIL')) {
      if (shouldClearEditorForAGEDETAIL(text)) {
        editor.innerHTML = ''
      }
    }

    const hasAgeDetailWithoutOnlyNumber = shouldClearEditorForAGEDETAIL(
      editor.innerText
    )

    if (hasAgeDetailWithoutOnlyNumber) {
      return
    }

    restoreCursorPosition()
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) return

    const range = selection.getRangeAt(0)
    let node = range.startContainer
    let offset = range.startOffset

    let parent = node as HTMLElement
    if (node.nodeType === Node.TEXT_NODE && node.parentElement) {
      parent = node.parentElement
    }

    if (!parent || !editor.contains(parent)) return

    if (editor.childElementCount === 0) {
      const initialDiv = document.createElement('i')
      initialDiv.textContent = '\u200B'
      editor.appendChild(initialDiv)
    }

    if (parent.closest('[contenteditable="false"]')) {
      const safePosition = document.createElement('i')

      parent.parentNode?.insertBefore(safePosition, parent.nextSibling)

      parent = safePosition
      node = safePosition.firstChild || safePosition
      offset = 0

      const newRange = document.createRange()
      newRange.setStart(node, offset)
      newRange.collapse(true)
      selection?.removeAllRanges()
      selection?.addRange(newRange)
    }

    const isNumber = /^[0-9.]+$/.test(text)
    const isOperator = /^[+\-*/]$/.test(text)
    const isParentheses = /^[()]$/.test(text)

    const isInsideEditor = editor.contains(range.commonAncestorContainer)

    if (isInsideEditor) {
      range.deleteContents()
    }

    let newElement: HTMLElement | null | undefined = null

    if (
      (isOperator || isParentheses) &&
      !['field', 'function'].includes(type || '')
    ) {
      newElement = insertElement(
        text,
        isOperator ? 'operator' : 'parentheses',
        parent,
        node,
        offset
      )
    }

    if (type === 'field') {
      const questionsFilteredWithoutDatetime = filteredQuestions.filter(
        (question) => {
          const questionType = question.question_type.question_type
          if (questionType === 'datetime') return false
          return true
        }
      )
      if (text[0] === 'R') {
        const { description, title, id } = getMatrixRowQuestionInfo(
          questionsFilteredWithoutDatetime,
          text
        )
        newElement = insertElement(title, 'field', parent, node, offset, {
          contentEditable: true,
          rowFormIndex: text,
          description,
          id,
        })
      } else {
        const { description, title, id } = getMatrixQuestionInfo(
          questionsFilteredWithoutDatetime,
          text
        )
        newElement = insertElement(title, 'field', parent, node, offset, {
          contentEditable: true,
          formIndex: text,
          description,
          id,
        })
      }
    }

    if (type === 'function') {
      const { qValues, rValues, qrPairs } = extractQRValues(text)

      const newDescription = buildFunctionDescriptionByFormula(
        filteredQuestions,
        text,
        qValues,
        rValues,
        qrPairs
      )

      newElement = insertElement(
        newDescription,
        'function',
        parent,
        node,
        offset,
        {
          contentEditable: true,
          value: text,
          description: newDescription,
          id: generateId(),
        }
      )
      setIsFunctionModalInfo(undefined)
    }

    if (isNumber && !['field', 'function'].includes(type || '')) {
      const nodeText = node.nodeValue || ''
      const hasOperatorOrParentheses = /[+\-*/()]/.test(nodeText)
      if (
        parent.dataset.type === 'number' &&
        node.nodeType === Node.TEXT_NODE &&
        !hasOperatorOrParentheses
      ) {
        const beforeText = nodeText.slice(0, offset)
        const afterText = nodeText.slice(offset)

        const newText = beforeText + text + afterText
        node.nodeValue = newText
        if (node.parentElement) {
          setCursorPosition(node.parentElement, offset + 1)
        }
      } else {
        newElement = insertElement(text, 'number', parent, node, offset)
      }
    }
    if (newElement) {
      if (parent.dataset.type === 'placeholder') {
        parent.remove()
      }
      setTimeout(() => {
        setCursorPosition(newElement, newElement.textContent?.length || 0)
      }, 0)
    }

    const lastChild = editor.lastElementChild as HTMLElement
    if (lastChild?.contentEditable === 'false') {
      const newContainer = document.createElement('i')
      editor.appendChild(newContainer)
    }

    saveCursorPosition()
  }

  const updateFunctionById = (newValue: string, id: string) => {
    const editor = editorRef.current
    if (!editor) return

    if (newValue.startsWith('AGEDETAIL')) {
      const needsClear = shouldClearEditorForAGEDETAIL(newValue)
      if (needsClear) {
        insertAtCursor(newValue, 'function')
        return
      }
    }

    const element = editor.querySelector(`div[data-id="${id}"]`) as HTMLElement

    const { qValues, rValues, qrPairs } = extractQRValues(newValue)
    const newDescription = buildFunctionDescriptionByFormula(
      filteredQuestions,
      newValue,
      qValues,
      rValues,
      qrPairs
    )

    if (element) {
      element.dataset.description = newDescription
      element.dataset.value = newValue
      element.textContent = newDescription
    }
  }

  const onSaveFunction = useCallback(
    (formula: string) => {
      if (isFunctionModalInfo?.id) {
        updateFunctionById(formula, isFunctionModalInfo.id)
      } else {
        insertAtCursor(formula, 'function')
      }
      setIsFunctionModalInfo(undefined)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isFunctionModalInfo]
  )

  const mouseOverEvent = (e: MouseEvent) => {
    const target = e.target as HTMLElement
    const tooltip = tooltipRef.current

    if (!tooltip || !target) return

    if (tooltipTimeoutId.current) {
      clearTimeout(tooltipTimeoutId.current)
    }

    if (
      target &&
      ['field', 'function'].includes(target.getAttribute('data-type') || '')
    ) {
      const rect = target.getBoundingClientRect()

      tooltip.style.display = 'block'
      tooltip.style.zIndex = '9999'
      tooltip.style.position = 'absolute'
      tooltip.style.top = `${rect.top}px`
      tooltip.style.left = `${rect.left}px`
      tooltip.style.width = `${rect.width}px`
      tooltip.style.height = '1px'

      tooltipTimeoutId.current = setTimeout(() => {
        setTooltipLabel(target.getAttribute('data-description') || '')
      }, 700)
    }
    setTooltipLabel(undefined)
  }

  const handleClickOutside = (event: MouseEvent) => {
    if (mainRef.current && !mainRef.current.contains(event.target as Node)) {
      setIsFocused(false)
    } else {
      setIsFocused(true)
    }
  }

  const handleClickInsideEditor = (event: MouseEvent) => {
    const target = event.target as HTMLElement
    if (!target) return
    const dataType = target.getAttribute('data-type')

    if (target.tagName === 'DIV' && dataType === 'function') {
      const dataValue = target.getAttribute('data-value') || ''
      const dataId = target.getAttribute('data-id') || ''

      if (dataValue) {
        if (tooltipTimeoutId.current) {
          clearTimeout(tooltipTimeoutId.current)
        }
        setTimeout(() => {
          setTooltipLabel(undefined)
        }, 200)
        setIsFunctionModalInfo({ open: true, value: dataValue, id: dataId })
      }
    }
  }

  const editorAddEventListeners = () => {
    const editor = editorRef.current
    if (!editor) return

    editor.addEventListener('mouseover', mouseOverEvent)
    editor.addEventListener('click', handleClickInsideEditor)
  }

  const removeEditorEventListeners = () => {
    const editor = editorRef.current
    if (!editor) return

    editor.removeEventListener('mouseover', mouseOverEvent)
    editor.removeEventListener('click', handleClickInsideEditor)
  }

  const observer = new MutationObserver(() => {
    const editable = editorRef.current
    const content = editable?.innerHTML

    if (!editable && !content) return
    onFormulaChange(parseFormulaFromEditor(editorRef) || '')
  })

  useEffect(() => {
    editorAddEventListeners()
    document.addEventListener('mousedown', handleClickOutside)

    const editor = editorRef.current
    if (!editor) return

    if (initialValues) {
      editor.innerHTML = convertFormulaToHtml(filteredQuestions, initialValues)
    }

    observer.observe(editor, {
      childList: true,
      subtree: true,
      characterData: true,
    })

    return () => {
      removeEditorEventListeners()
      document.removeEventListener('mousedown', handleClickOutside)
      observer.disconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isDisabled = !isFocused && !lastRange

  return (
    <div className="mt-4 flex flex-col gap-3" ref={mainRef}>
      <CalculationAddField
        addField={(value) => insertAtCursor(String(value), 'field')}
        questions={filteredQuestions}
      />
      <div className="rounded-md border border-border">
        <CalculationKeyboard.Root className="grid-cols-9">
          {buttons.map((button, index) => {
            const keyboardButtonStyle = {
              '!rounded-tl-md': index === 0,
              '!rounded-tr-md': index === 8,
            }
            return (
              <CalculationKeyboard.Button
                className={cn(keyboardButtonStyle)}
                disabled={isDisabled}
                key={button}
                onClick={() => insertAtCursor(button)}
              >
                {button}
              </CalculationKeyboard.Button>
            )
          })}
          <Tooltip mouseEnterDelay={0.7} title="Add a function">
            <CalculationKeyboard.Button
              disabled={isDisabled}
              onClick={() => setIsFunctionModalInfo({ open: true })}
            >
              <Function className="text-xl" weight="bold" />
            </CalculationKeyboard.Button>
          </Tooltip>
        </CalculationKeyboard.Root>
        <CalculationEditor
          disabled={isDisabled}
          onBlur={saveCursorPosition}
          onFocus={() => setIsFocused(true)}
          onKeyDown={handleKeyDown}
          ref={editorRef}
        />
        <div ref={tooltipRef}>
          <Tooltip label={tooltipLabel} open={Boolean(tooltipLabel)}>
            <span className="block h-[1px] w-full" />
          </Tooltip>
        </div>
      </div>
      {isFunctionModalInfo?.open ? (
        <CalculationFunctionModal
          {...{
            questions: filteredQuestions,
            isOpen: Boolean(isFunctionModalInfo?.open),
            initialValues: isFunctionModalInfo?.value,
            onClose: () => setIsFunctionModalInfo(undefined),
            onSave: onSaveFunction,
          }}
        />
      ) : null}
    </div>
  )
}
